/**
 * CoachAnalyticsDashboard — P16.5
 *
 * Four-tab analytics dashboard for coaches:
 *   Tab 1 — At-Risk Learners
 *   Tab 2 — Scene Heatmap
 *   Tab 3 — Overlay Efficacy
 *   Tab 4 — Confusion Clusters
 */
import React, { useState, useEffect, useCallback } from 'react';
import CoachLayout from './CoachLayout';
import { useAuth } from '../../context/AuthContext';
import { curriculumAPI } from '../../services/api';
import useOrgScope from '../../hooks/useOrgScope';
import CurriculumScenePicker from '../../components/coach/CurriculumScenePicker';

function getToken() {
  return localStorage.getItem('scenario_os_token') || '';
}

async function apiFetch(url) {
  const token = getToken();
  const resp = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: resp.statusText }));
    throw new Error(err?.detail || `HTTP ${resp.status}`);
  }
  return resp.json();
}

// ── Shared UI ─────────────────────────────────────────────────────────────────

function TabBar({ tabs, active, onSelect }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #e5e7eb', paddingBottom: 0 }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onSelect(t.id)}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderBottom: active === t.id ? '2px solid #4f46e5' : '2px solid transparent',
            background: 'none',
            color: active === t.id ? '#4f46e5' : '#6b7280',
            fontWeight: active === t.id ? 700 : 400,
            fontSize: 14,
            cursor: 'pointer',
            marginBottom: -2,
            transition: 'color 0.15s',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      border: '1px solid #e5e7eb',
      padding: 24,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      ...style,
    }}>
      {children}
    </div>
  );
}

function StatusMsg({ loading, error }) {
  if (loading) return <div style={{ color: '#6b7280', fontSize: 14, padding: '24px 0' }}>Loading…</div>;
  if (error) return <div style={{ color: '#dc2626', fontSize: 14, padding: '12px 0' }}>{error}</div>;
  return null;
}

// ── Tab 1: At-Risk Learners ───────────────────────────────────────────────────

function AtRiskTab({ domains = [] }) {
  const [domain, setDomain] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Select first available domain when list loads
  useEffect(() => {
    if (domains.length && !domain) {
      setDomain(domains[0]);
    }
  }, [domains, domain]);

  const fetchData = useCallback(async (d) => {
    if (!d) {
      setData({ at_risk: [], total: 0 });
      setError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await apiFetch(
        `/scenario-os/metrics/coach/at-risk/${encodeURIComponent(d)}?min_sessions=1&inactive_days=7`
      );
      setData(result);
    } catch (e) {
      setError(e.message || 'Failed to load at-risk data.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(domain); }, [domain, fetchData]);

  function riskLevel(row) {
    const days = row.inactive_days ?? 0;
    if (days > 14) return { label: 'High', bg: '#fef2f2', color: '#dc2626' };
    if (days > 7)  return { label: 'Medium', bg: '#fffbeb', color: '#d97706' };
    return { label: 'Low', bg: '#f0fdf4', color: '#16a34a' };
  }

  const atRisk = data?.at_risk || [];

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Domain:</label>
        <select
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          style={{
            padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db',
            fontSize: 14, background: '#f9fafb', cursor: 'pointer',
          }}
        >
          {domains.map((d) => (
            <option key={d} value={d}>{d.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
          ))}
        </select>
      </div>

      <StatusMsg loading={loading} error={error} />

      {!loading && !error && data && (
        <>
          <div style={{ marginBottom: 12, fontSize: 14, color: '#6b7280' }}>
            <strong style={{ color: '#111827' }}>{data.total}</strong> at-risk learner{data.total !== 1 ? 's' : ''} in <em>{domain}</em>
          </div>
          {atRisk.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: 14 }}>No at-risk learners found.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Learner', 'Sessions', 'Last Active', 'Inactive Days', 'Risk Level'].map((h) => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {atRisk.map((row, i) => {
                    const risk = riskLevel(row);
                    return (
                      <tr key={row.learner_id + i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '8px 12px', color: '#1f2937' }}>
                          <div style={{ fontWeight: 600 }}>{row.display_name || row.learner_id}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{row.learner_id}</div>
                        </td>
                        <td style={{ padding: '8px 12px', color: '#374151' }}>{row.session_count ?? 0}</td>
                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>{row.last_active_at ? new Date(row.last_active_at).toLocaleDateString() : (row.last_active ? new Date(row.last_active).toLocaleDateString() : '—')}</td>
                        <td style={{ padding: '8px 12px', color: '#374151' }}>{row.inactive_days ?? '—'}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{ background: risk.bg, color: risk.color, borderRadius: 4, padding: '2px 8px', fontWeight: 600, fontSize: 12 }}>
                            {risk.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function SceneDrilldownForm({ domains, buttonLabel, onLoad }) {
  const [sel, setSel] = useState({ sceneId: '' });
  return (
    <div style={{ marginBottom: 24 }}>
      <CurriculumScenePicker domains={domains} onChange={setSel} style={{ marginBottom: 12 }} />
      <button
        type="button"
        disabled={!sel.sceneId}
        onClick={() => onLoad(sel.sceneId)}
        style={{
          padding: '9px 20px', borderRadius: 6, border: 'none', background: '#4f46e5',
          color: '#fff', fontWeight: 600, fontSize: 14, cursor: sel.sceneId ? 'pointer' : 'not-allowed',
          opacity: sel.sceneId ? 1 : 0.5,
        }}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

// ── Tab 2: Scene Heatmap ─────────────────────────────────────────────────────

function SceneHeatmapTab({ domains = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (sid) => {
    if (!sid) return;
    setLoading(true);
    setError('');
    try {
      const result = await apiFetch(`/scenario-os/metrics/coach/scene-heatmap/${encodeURIComponent(sid)}`);
      setData(result);
    } catch (e) {
      setError(e.message || 'Failed to load heatmap data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const buckets = data?.buckets || [];
  const maxVal = Math.max(...buckets.map((b) => (b.pause_count || 0) + (b.replay_count || 0)), 1);

  return (
    <Card>
      <SceneDrilldownForm domains={domains} buttonLabel="Load Heatmap" onLoad={fetchData} />

      <StatusMsg loading={loading} error={error} />

      {!loading && !error && data && (
        <>
          <div style={{ marginBottom: 12, fontSize: 13, color: '#6b7280' }}>
            Scene: <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: 3 }}>{data.scene_id}</code>
            &nbsp;· {data.total_events} events · {buckets.length} buckets
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 12 }}>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#3b82f6', marginRight: 4 }} />Pauses</span>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#f97316', marginRight: 4 }} />Replays</span>
          </div>
          {buckets.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: 14 }}>No heatmap events yet for this scene.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {buckets.map((b) => {
                const total = (b.pause_count || 0) + (b.replay_count || 0);
                const pausePct = maxVal > 0 ? ((b.pause_count || 0) / maxVal) * 100 : 0;
                const replayPct = maxVal > 0 ? ((b.replay_count || 0) / maxVal) * 100 : 0;
                const label = `${Math.round((b.bucket_start_ms || 0) / 1000)}s`;
                return (
                  <div key={b.bucket_start_ms} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 50, fontSize: 12, color: '#6b7280', textAlign: 'right', flexShrink: 0 }}>{label}</div>
                    <div style={{ flex: 1, display: 'flex', height: 18, borderRadius: 3, overflow: 'hidden', background: '#f3f4f6' }}>
                      <div style={{ width: `${pausePct}%`, background: '#3b82f6', transition: 'width 0.3s' }} title={`${b.pause_count} pauses`} />
                      <div style={{ width: `${replayPct}%`, background: '#f97316', transition: 'width 0.3s' }} title={`${b.replay_count} replays`} />
                    </div>
                    <div style={{ width: 40, fontSize: 12, color: '#374151', textAlign: 'right', flexShrink: 0 }}>{total}</div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ── Tab 3: Overlay Efficacy ───────────────────────────────────────────────────

function OverlayEfficacyTab({ domains = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (sid) => {
    if (!sid) return;
    setLoading(true);
    setError('');
    try {
      const result = await apiFetch(`/scenario-os/metrics/coach/overlay-efficacy/${encodeURIComponent(sid)}`);
      setData(result);
    } catch (e) {
      setError(e.message || 'Failed to load overlay efficacy data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const markers = data?.markers || [];

  return (
    <Card>
      <SceneDrilldownForm domains={domains} buttonLabel="Load Efficacy" onLoad={fetchData} />

      <StatusMsg loading={loading} error={error} />

      {!loading && !error && data && (
        <>
          <div style={{ marginBottom: 12, fontSize: 13, color: '#6b7280' }}>
            Scene: <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: 3 }}>{data.scene_id}</code>
            &nbsp;· {markers.length} markers
          </div>
          {markers.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: 14 }}>No overlay data yet for this scene.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Marker ID', 'Overlay Type', 'Answered', 'Skipped', 'Avg Attempts', 'Correct Rate (%)'].map((h) => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {markers.map((m, i) => {
                    const lowScore = (m.correct_rate ?? 0) < 0.5;
                    return (
                      <tr key={(m.marker_id || '') + i} style={{ borderBottom: '1px solid #f3f4f6', background: lowScore ? '#fff3cd' : 'transparent' }}>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#1f2937' }}>{m.marker_id ?? '—'}</td>
                        <td style={{ padding: '8px 12px', color: '#374151' }}>{m.overlay_type ?? '—'}</td>
                        <td style={{ padding: '8px 12px', color: '#374151' }}>{m.answered ?? 0}</td>
                        <td style={{ padding: '8px 12px', color: '#374151' }}>{m.skipped ?? 0}</td>
                        <td style={{ padding: '8px 12px', color: '#374151' }}>{(m.avg_attempts ?? 0).toFixed(2)}</td>
                        <td style={{ padding: '8px 12px', color: lowScore ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                          {((m.correct_rate ?? 0) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ── Tab 4: Confusion Clusters ─────────────────────────────────────────────────

function ConfusionClustersTab({ domains = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (sid) => {
    if (!sid) return;
    setLoading(true);
    setError('');
    try {
      const result = await apiFetch(`/scenario-os/metrics/coach/confusion-clusters/${encodeURIComponent(sid)}`);
      setData(result);
    } catch (e) {
      setError(e.message || 'Failed to load confusion cluster data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const clusters = data?.clusters || [];

  function clusterBadge(count) {
    if (count > 10) return { bg: '#fef2f2', color: '#dc2626' };
    if (count > 5)  return { bg: '#fffbeb', color: '#d97706' };
    return { bg: '#f0f9ff', color: '#2563eb' };
  }

  return (
    <Card>
      <SceneDrilldownForm domains={domains} buttonLabel="Find Clusters" onLoad={fetchData} />

      <StatusMsg loading={loading} error={error} />

      {!loading && !error && data && (
        <>
          <div style={{ marginBottom: 16, fontSize: 13, color: '#6b7280' }}>
            Scene: <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: 3 }}>{data.scene_id}</code>
            &nbsp;· <strong style={{ color: '#111827' }}>{data.total_confused_annotations}</strong> total confused annotations
            &nbsp;· <strong style={{ color: '#111827' }}>{clusters.length}</strong> clusters
          </div>
          {clusters.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: 14 }}>No confusion clusters found (minimum 3 annotations per cluster).</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {clusters.map((c, i) => {
                const badge = clusterBadge(c.count);
                return (
                  <div
                    key={c.anchor + i}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: '12px 16px',
                      display: 'flex',
                      gap: 16,
                      alignItems: 'flex-start',
                    }}
                  >
                    <div style={{ flexShrink: 0 }}>
                      <div style={{
                        background: badge.bg, color: badge.color,
                        borderRadius: 999, padding: '2px 10px',
                        fontWeight: 700, fontSize: 16, textAlign: 'center',
                        minWidth: 40,
                      }}>
                        {c.count}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: 2 }}>annots.</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', fontFamily: 'monospace' }}>
                          {c.anchor}
                        </span>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                          {c.learners} learner{c.learners !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {(c.sample_texts || []).filter(Boolean).length > 0 && (
                        <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.5 }}>
                          {c.sample_texts.filter(Boolean).map((t, j) => (
                            <div key={j} style={{ marginBottom: 2 }}>
                              <span style={{ color: '#9ca3af', marginRight: 4 }}>›</span>"{t}"
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'at-risk',    label: 'At-Risk Learners' },
  { id: 'heatmap',   label: 'Scene Heatmap' },
  { id: 'efficacy',  label: 'Overlay Efficacy' },
  { id: 'confusion', label: 'Confusion Clusters' },
];

export default function CoachAnalyticsDashboard() {
  const { authoringDomains, isPlatformAdmin } = useAuth();
  const { domains: scopeDomains, unrestricted, loading: scopeLoading, expandAliases } = useOrgScope();
  const [activeTab, setActiveTab] = useState('at-risk');
  const [entitledDomains, setEntitledDomains] = useState([]);
  const [domainCatalog, setDomainCatalog] = useState([]);

  useEffect(() => {
    if (scopeLoading) return;
    curriculumAPI.listDomains()
      .then(({ data }) => {
        const catalog = Array.isArray(data?.domains) ? data.domains : [];
        setDomainCatalog(catalog);
        if (isPlatformAdmin || unrestricted) {
          setEntitledDomains(catalog.map((d) => d.domain_id).filter(Boolean));
          return;
        }
        const fromScope = (scopeDomains || []).map((d) => d.domain_id).filter(Boolean);
        const allowed = expandAliases(fromScope.length ? fromScope : (authoringDomains || []));
        setEntitledDomains(
          catalog.map((d) => d.domain_id).filter((id) => id && allowed.has(id)),
        );
      })
      .catch(() => {
        const fromScope = (scopeDomains || []).map((d) => d.domain_id).filter(Boolean);
        const fallback = fromScope.length ? fromScope : (authoringDomains || []);
        setEntitledDomains(fallback);
        setDomainCatalog(fallback.map((id) => ({ domain_id: id, display_name: id })));
      });
  }, [isPlatformAdmin, unrestricted, scopeDomains, authoringDomains, scopeLoading, expandAliases]);

  const pickerDomains = domainCatalog.length
    ? domainCatalog.filter((d) => !entitledDomains.length || entitledDomains.includes(d.domain_id))
    : entitledDomains;

  return (
    <CoachLayout title="Analytics Dashboard">
      <TabBar tabs={TABS} active={activeTab} onSelect={setActiveTab} />
      {activeTab === 'at-risk'    && <AtRiskTab domains={entitledDomains} />}
      {activeTab === 'heatmap'    && <SceneHeatmapTab domains={pickerDomains} />}
      {activeTab === 'efficacy'   && <OverlayEfficacyTab domains={pickerDomains} />}
      {activeTab === 'confusion'  && <ConfusionClustersTab domains={pickerDomains} />}
    </CoachLayout>
  );
}
